/**
 * Max viewport width (px) treated as mobile. Mirrors the SCSS `$mobile-breakpoint` in
 * `src/assets/styles/_vars.scss` — keep the two in step, because every consumer of
 * {@link isMobileViewport} is deciding whether an element a media query hides at this same width is
 * there to interact with.
 */
export const MOBILE_BREAKPOINT_PX = 768;

/**
 * True when the current viewport is mobile-width, i.e. narrow enough that `@media (max-width:
 * $mobile-breakpoint)` rules apply.
 *
 * 🔴 Deliberately NOT `MediaService.isMobile()`, which tests against `LayoutEnum.MOBILE_MEDIA_WIDTH = 992`
 * and would call a 900px-wide desktop window "mobile". A component whose mobile behaviour is paired with a
 * `$mobile-breakpoint` media query has to test the same 768px the stylesheet does, or the TypeScript and the
 * CSS disagree across the 769–992px band.
 *
 * Call this from a **getter**, not a field, so it is re-read per change-detection cycle and the component
 * follows a rotate without needing its own resize listener.
 *
 * The `typeof window` guard keeps this safe to evaluate outside a browser (a non-DOM test environment).
 * @returns {boolean} whether the viewport is at or below {@link MOBILE_BREAKPOINT_PX}
 */
export function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX;
}
